import * as React from 'react';
import { useCallback } from 'react';
import type { FormikProps } from 'formik';
import * as Immutable from 'immutable';

import type Search from 'views/logic/search/Search';
import SearchBar from 'data-lake/preview/types/internal/SearchBar';
import type { ActivePreviewSearch, DataLakeURLQueryParams } from 'data-lake/preview/Types';
import DataLakePreview from 'data-lake/preview/DataLakePreview';
import DataLakeWidgetConfig from 'data-lake/logic/DataLakeWidgetConfig';
import useInitialSearchBarFormValues from 'data-lake/preview/types/internal/hooks/useInitialFormValues';
import { WIDGET_DEFAULTS, MAX_LOADED_MESSAGES, INTERNAL_DATA_LAKE_SEARCH_TYPE } from 'data-lake/preview/Constants';
import { onInitializingTimerange } from 'views/components/TimerangeForForm';
import useUserDateTime from 'hooks/useUserDateTime';
import { normalizeFromSearchBarForBackend } from 'views/logic/queries/NormalizeTimeRange';
import type DataLakeWidget from 'data-lake/logic/DataLakeWidget';
import Query from 'views/logic/queries/Query';
import generateId from 'logic/generateId';
import StreamFilter from 'data-lake/preview/StreamFilter';
import type { InternalDataLakeFormValues, InternalDataLakeSearchType } from 'data-lake/preview/types/internal/Types';
import createInitialWidget from 'data-lake/preview/types/internal/createInitialWidget';
import { INTERNAL_DATA_LAKE_QUERY } from 'data-lake/preview/types/internal/Constants';

const validate = (formValues: InternalDataLakeFormValues) => {
  const errors = {};

  if (!formValues.stream) {
    return { ...errors, stream: 'Stream is required' };
  }

  return errors;
};

type InternalDataLakeSearchBarProps = {
  executedSearch: Search | undefined;
  onReset: () => void;
  onSubmit: (formValues: InternalDataLakeFormValues) => void;
  formRef?: React.RefObject<FormikProps<InternalDataLakeFormValues>>;
  searchJobIsRunning: boolean;
  initialUrlParams: DataLakeURLQueryParams;
};

const InternalDataLakeSearchBar = ({
  executedSearch,
  onReset,
  onSubmit,
  formRef = undefined,
  searchJobIsRunning,
  initialUrlParams,
}: InternalDataLakeSearchBarProps) => {
  const initialFormValues = useInitialSearchBarFormValues(executedSearch, initialUrlParams);

  return (
    <SearchBar
      validate={validate}
      rightColumn={<StreamFilter />}
      emptyFormValues={{
        timerange: WIDGET_DEFAULTS.timerange,
        stream: undefined,
        fields: undefined,
      }}
      initialValues={initialFormValues}
      onReset={onReset}
      onSubmit={onSubmit}
      formRef={formRef}
      searchJobIsRunning={searchJobIsRunning}
    />
  );
};

type Props = {
  initialSearch: Search | undefined;
  initialSearchJob: { status: ActivePreviewSearch['status']; id: string; nodeId: string; progress: number } | undefined;
  initialUrlParams: DataLakeURLQueryParams;
  resetUrlQueryParams: () => void;
};

const InternalDataLakePreview = ({ initialSearch, initialSearchJob, initialUrlParams, resetUrlQueryParams }: Props) => {
  const { formatTime, userTimezone } = useUserDateTime();
  const renderSearchBar = useCallback(
    ({
      executedSearch,
      onReset,
      onSubmit,
      searchJobIsRunning,
      formRef,
    }: Omit<InternalDataLakeSearchBarProps, 'initialUrlParams'>) => (
      <InternalDataLakeSearchBar
        formRef={formRef}
        executedSearch={executedSearch}
        onReset={onReset}
        onSubmit={onSubmit}
        searchJobIsRunning={searchJobIsRunning}
        initialUrlParams={initialUrlParams}
      />
    ),
    [initialUrlParams],
  );

  const widgetFromFormValues = useCallback(
    (widget: DataLakeWidget, { fields, timerange, stream }: InternalDataLakeFormValues) => {
      const updatedWidgetConfig: DataLakeWidgetConfig = widget.config
        .toBuilder()
        .fieldFilters(fields?.fieldFilters ? Immutable.List(fields.fieldFilters) : widget.config.fieldFilters)
        .filtersOperator(fields?.operator ?? widget.config.filtersOperator)
        .build();

      return widget
        .toBuilder()
        .config(updatedWidgetConfig)
        .timerange(timerange ? normalizeFromSearchBarForBackend(timerange, userTimezone) : widget.timerange)
        .streams([stream])
        .build();
    },
    [userTimezone],
  );

  const filtersFromSearchType = useCallback(
    (searchType: InternalDataLakeSearchType) => ({
      stream: searchType.streams[0],
      timerange: onInitializingTimerange(searchType.timerange, formatTime),
      fields: {
        fieldFilters: searchType.field_filters ?? WIDGET_DEFAULTS.fieldFilters,
        operator: searchType.filters_operator ?? WIDGET_DEFAULTS.fieldsOperator,
      },
    }),
    [formatTime],
  );
  const queryFromFormValues = useCallback(
    (widget: DataLakeWidget, { stream }: InternalDataLakeFormValues) =>
      Query.builder()
        .id(generateId())
        .query(INTERNAL_DATA_LAKE_QUERY)
        .searchTypes([
          {
            size: MAX_LOADED_MESSAGES,
            filters_operator: widget.config.filtersOperator,
            timerange: widget.timerange,
            streams: [stream],
            field_filters: widget.config.fieldFilters.toArray(),
            fields: widget.config.fields.toArray(),
            type: INTERNAL_DATA_LAKE_SEARCH_TYPE,
            filters: [],
          } as unknown as InternalDataLakeSearchType,
        ])
        .build(),
    [],
  );

  const widgetFromSearch = useCallback((search: Search) => createInitialWidget(search), []);

  return (
    <DataLakePreview
      createInitialWidget={widgetFromSearch}
      defaultWidgetFields={DataLakeWidgetConfig.defaultFields}
      filtersFromSearchType={filtersFromSearchType}
      initialSearch={initialSearch}
      initialSearchJob={initialSearchJob}
      queryFromFormValues={queryFromFormValues}
      resetUrlQueryParams={resetUrlQueryParams}
      searchBar={renderSearchBar}
      widgetFromFormValues={widgetFromFormValues}
    />
  );
};

export default InternalDataLakePreview;
